import { useRef, useEffect, useState, useCallback } from 'react';
import { Mic, MicOff, Send, Brain, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function CloneAvatarChat({ personId, cloneName, onGoToBrain }) {
  const containerRef = useRef(null);
  const headRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [listening, setListening] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [inputText, setInputText] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [userText, setUserText] = useState('');
  const recognitionRef = useRef(null);

  /* ═══ Load TalkingHead ═══ */
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const { TalkingHead } = await import(
          /* webpackIgnore: true */
          'https://cdn.jsdelivr.net/gh/met4citizen/TalkingHead@1.7/modules/talkinghead.mjs'
        );

        if (cancelled) return;

        const head = new TalkingHead(containerRef.current, {
          lipsyncModules: ['en'],
          cameraView: 'upper',
        });

        await head.showAvatar(
          {
            url: '/test-avatar.glb',
            body: 'F',
            avatarMood: 'neutral',
            lipsyncLang: 'en',
          },
          (ev) => {
            if (ev.lengthComputable) {
              setLoadProgress(Math.round((ev.loaded / ev.total) * 100));
            }
          }
        );

        headRef.current = head;
        setLoading(false);

        // Greeting after load
        setTimeout(() => {
          const firstName = cloneName?.split(' ')[0] || 'Justine';
          setSubtitle(`Bonjour, je suis le clone de ${firstName}. Posez-moi une question.`);
        }, 1000);
      } catch (err) {
        console.error('TalkingHead init error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
      headRef.current?.stop();
    };
  }, [cloneName]);

  /* ═══ Chat with clone RAG ═══ */
  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim() || processing) return;

      setProcessing(true);
      setUserText(text);
      setSubtitle('...');
      setInputText('');

      // Show thinking mood
      try {
        headRef.current?.setMood('thinking');
      } catch (_) {}

      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/persons/${personId}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message: text }),
        });

        if (!res.ok) throw new Error(`API ${res.status}`);
        const data = await res.json();
        const reply = data.response || data.message || "Je n'ai pas compris.";

        setSubtitle(reply);

        // Generate TTS audio and make avatar speak with lip-sync
        try {
          headRef.current?.setMood('neutral');

          const ttsRes = await fetch(`${API}/api/persons/${personId}/speak`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ text: reply, format: 'mp3' }),
          });

          if (ttsRes.ok) {
            const ttsData = await ttsRes.json();
            const audioB64 = ttsData.audio;
            // Convert base64 to Blob for TalkingHead
            const binaryString = atob(audioB64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const audioBlob = new Blob([bytes], { type: 'audio/mp3' });
            headRef.current?.speakAudio(audioBlob);
          } else {
            console.warn('TTS failed, avatar stays silent');
          }
        } catch (speakErr) {
          console.warn('Speak error (non-blocking):', speakErr);
        }
      } catch (err) {
        console.error('Chat error:', err);
        setSubtitle("Erreur de communication avec le clone.");
        try {
          headRef.current?.setMood('sad');
        } catch (_) {}
      } finally {
        setProcessing(false);
      }
    },
    [personId, processing]
  );

  /* ═══ Speech Recognition (Push-to-Talk) ═══ */
  const toggleMic = useCallback(() => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSubtitle('Speech recognition non disponible dans ce navigateur.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setListening(false);
      sendMessage(transcript);
    };

    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
    setUserText('');
    setSubtitle('Je vous ecoute...');
  }, [listening, sendMessage]);

  /* ═══ Render ═══ */
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      data-testid="clone-avatar-chat"
      style={{ background: '#0a0e1a' }}
    >
      {/* ── Avatar container ── */}
      <div className="flex-1 relative overflow-hidden">
        <div
          ref={containerRef}
          data-testid="avatar-3d-container"
          className="absolute inset-0"
          style={{ background: '#0a0e1a' }}
        />

        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-[#0a0e1a]">
            <Loader2 className="w-8 h-8 text-blue-400/60 animate-spin mb-3" />
            <p className="text-blue-300/50 text-sm font-mono tracking-wider">
              Chargement de l&apos;avatar... {loadProgress}%
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-[#0a0e1a]">
            <p className="text-red-400/70 text-sm max-w-md text-center px-6">
              Erreur: {error}
            </p>
          </div>
        )}

        {/* Scan lines overlay */}
        <div
          className="absolute inset-0 pointer-events-none z-[2]"
          style={{
            background:
              'repeating-linear-gradient(0deg, transparent 0px, transparent 3px, rgba(0,30,80,0.015) 3px, rgba(0,30,80,0.015) 4px)',
          }}
        />

        {/* Vignette */}
        <div
          className="absolute inset-0 pointer-events-none z-[3]"
          style={{
            background:
              'radial-gradient(ellipse 70% 80% at 50% 45%, transparent 40%, rgba(10,14,26,0.7) 70%, rgba(10,14,26,0.95) 100%)',
          }}
        />

        {/* Clone name */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
          <span className="text-[10px] font-mono tracking-[0.45em] text-blue-300/20 uppercase">
            {cloneName}
          </span>
        </div>

        {/* Go to Brain button */}
        <button
          onClick={onGoToBrain}
          data-testid="go-to-brain-btn"
          className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full
                     text-[10px] text-blue-300/40 hover:text-blue-300/70
                     border border-blue-400/10 hover:border-blue-400/25
                     bg-blue-500/[0.02] hover:bg-blue-500/[0.06]
                     transition-all duration-300 backdrop-blur-sm"
        >
          <Brain className="w-3 h-3" />
          Explorer le cerveau
        </button>
      </div>

      {/* ── Subtitle area ── */}
      <div className="relative z-10 px-6 py-2 min-h-[60px] flex flex-col items-center justify-center">
        {userText && (
          <p className="text-[12px] text-blue-200/30 mb-1 italic">
            {userText}
          </p>
        )}
        {subtitle && (
          <p
            className="text-[15px] text-white/60 font-light text-center leading-relaxed max-w-2xl"
            style={{ textShadow: '0 0 20px rgba(0,0,0,0.8)' }}
          >
            {subtitle}
          </p>
        )}
      </div>

      {/* ── Input bar ── */}
      <div className="relative z-10 px-4 pb-4 flex items-center gap-3 max-w-2xl mx-auto w-full">
        {/* Mic button */}
        <button
          onClick={toggleMic}
          data-testid="avatar-mic-btn"
          disabled={processing}
          className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center
                      border transition-all duration-300 ${
                        listening
                          ? 'bg-red-500/20 border-red-400/40 text-red-300 animate-pulse'
                          : 'bg-blue-500/[0.05] border-blue-400/15 text-blue-300/50 hover:text-blue-300/80 hover:border-blue-400/30'
                      }`}
        >
          {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>

        {/* Text input */}
        <div className="flex-1 relative">
          <input
            type="text"
            data-testid="avatar-text-input"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inputText.trim()) sendMessage(inputText);
            }}
            placeholder="Posez une question au clone..."
            disabled={processing}
            className="w-full h-12 px-4 pr-12 rounded-full bg-white/[0.04] border border-white/[0.06]
                       text-white/70 text-sm placeholder:text-white/20
                       focus:outline-none focus:border-blue-400/30 focus:bg-white/[0.06]
                       transition-all duration-300"
          />
          <button
            onClick={() => inputText.trim() && sendMessage(inputText)}
            data-testid="avatar-send-btn"
            disabled={processing || !inputText.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full
                       flex items-center justify-center text-blue-300/40 hover:text-blue-300/70
                       transition-colors duration-200 disabled:opacity-30"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
